import { useCallback, useEffect, useRef } from 'react';
import { formatMessage } from '../../../translations/format-message';

const LED_ERROR_MESSAGE_KEYS: Record<string, string> = {
  INVALID_FORMAT: 'led_error_invalid_format',
  INVALID_TOTAL_LENGTH: 'led_error_invalid_total_length',
  TOTAL_LENGTH_EXCEEDS_HARDWARE: 'led_error_total_length_exceeds_hardware',
  MISSING_TIME: 'led_error_missing_time',
  INVALID_TIME: 'led_error_invalid_time',
  INVALID_POINT_COUNT: 'led_error_invalid_point_count',
  INVALID_COLOR: 'led_error_invalid_color',
  INVALID_LENGTH: 'led_error_invalid_length',
  LENGTH_OUT_OF_BOUNDS: 'led_error_length_out_of_bounds',
};

const getLedErrorMessage = (code: string): string => {
  const messageKey = LED_ERROR_MESSAGE_KEYS[code];
  return messageKey
    ? formatMessage(messageKey)
    : `${formatMessage('led_error_unknown')} (${code})`;
};

const isAckLine = (line: string): boolean =>
  line.startsWith('OK') || line.startsWith('ERROR:');

const ACK_TIMEOUT_MS = 10000;
const CONNECT_SETTLE_MS = 1000;
// Text that must appear in SerialPort.getInfo() (JSON, e.g. usbVendorId/usbProductId)
// to identify the LED device. TODO: replace with the real value.
const LED_PORT_INFO_TEXT = 'usbVendorId';

// Shared across hook instances (table components and the stop handler each own one).
let lastLedMessage: string | null = null;

/** Forget the remembered command, e.g. when the active tray changes. */
export const clearLastLedMessage = () => {
  lastLedMessage = null;
};

/**
 * Builds the "stop glow" command: the last successfully sent command with its
 * time field set to 0, so the device keeps the previous LED values.
 * Returns null when no command has been sent yet.
 */
export const buildStopLedMessage = (): string | null =>
  lastLedMessage ? lastLedMessage.replace(/^([^,]*),[^,]*/, '$1,0') : null;

/**
 * Handles connecting to, writing to, and reading acknowledgements from
 * the LED tape measurement device over the Web Serial API.
 */
export const useLedSerial = () => {
  const portRef = useRef<SerialPort | null>(null);
  const writerRef = useRef<WritableStreamDefaultWriter<Uint8Array> | null>(
    null
  );
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(
    null
  );
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());

  const disconnectSerial = useCallback(async () => {
    try {
      if (readerRef.current) {
        try {
          await readerRef.current.cancel();
        } catch {
          // reader may already be closed
        }
        readerRef.current = null;
      }

      if (writerRef.current) {
        writerRef.current.releaseLock();
        writerRef.current = null;
      }

      if (portRef.current) {
        await portRef.current.close();
        portRef.current = null;
        console.log('Serial port disconnected');
      }
    } catch (error) {
      console.error('Serial disconnect error:', error);
    }
  }, []);

  const connectSerial = useCallback(async () => {
    if (!('serial' in navigator)) {
      throw new Error(formatMessage('led_serial_not_supported'));
    }

    if (portRef.current?.readable && portRef.current?.writable) {
      return;
    }

    // Prefer an already-granted port whose info contains the fixed text;
    // otherwise fall back to the port picker.
    let port: SerialPort | undefined;
    const grantedPorts = await navigator.serial.getPorts();
    console.log(
      'Granted serial ports:',
      grantedPorts.map((p, i) => ({ index: i, info: p.getInfo() }))
    );
    for (const candidate of grantedPorts) {
      const info = JSON.stringify(candidate.getInfo()).toLowerCase();
      if (info.includes(LED_PORT_INFO_TEXT.toLowerCase())) {
        port = candidate;
        break;
      }
    }
    if (!port) {
      port = await navigator.serial.requestPort();
    }
    console.log('Selected serial port info:', port.getInfo());
    await port.open({ baudRate: 115200 });

    if (!port.writable || !port.readable) {
      await port.close();
      throw new Error(formatMessage('led_serial_not_writable'));
    }

    portRef.current = port;
    writerRef.current = port.writable.getWriter();
    console.log('Serial port connected');
  }, []);

  const readAckLine = useCallback(async (): Promise<string> => {
    const port = portRef.current;
    if (!port?.readable) {
      throw new Error(formatMessage('led_serial_not_writable'));
    }

    const reader = port.readable.getReader();
    readerRef.current = reader;

    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      reader.cancel().catch(() => {
        // reader may already be closed
      });
    }, ACK_TIMEOUT_MS);

    let buffer = '';
    try {
      let done = false;
      while (!done) {
        const result = await reader.read();
        done = result.done;
        if (done) break;

        buffer += new TextDecoder().decode(result.value);

        // Skip any line that is not an OK / ERROR acknowledgement.
        let newlineIndex = buffer.indexOf('\n');
        while (newlineIndex !== -1) {
          const line = buffer.slice(0, newlineIndex).replace(/\r$/, '').trim();
          buffer = buffer.slice(newlineIndex + 1);
          if (isAckLine(line)) return line;
          newlineIndex = buffer.indexOf('\n');
        }
      }

      if (timedOut) {
        throw new Error(formatMessage('led_error_no_response'));
      }

      const remaining = buffer.trim();
      return isAckLine(remaining) ? remaining : '';
    } finally {
      clearTimeout(timeoutId);
      reader.releaseLock();
      readerRef.current = null;

      if (timedOut) {
        await disconnectSerial();
      }
    }
  }, [disconnectSerial]);

  /**
   * Sends a command to the LED device and resolves once "OK" is
   * received. Rejects with a user-facing message when the device
   * responds with "ERROR:<code>" per the device's protocol.
   */
  const sendCommand = useCallback(
  (message: string, options?: { final?: boolean }) => {
    const final = options?.final ?? false;
    const run = async () => {
      console.log('LED command sending:', JSON.stringify(message), { final });
      if (!writerRef.current) {
        // A final command (Pause/Complete) must never open a new connection.
        if (final) throw new Error(formatMessage('led_serial_not_writable'));
        await connectSerial();
        // Give the device time to settle after the port opens.
        await new Promise((resolve) => setTimeout(resolve, CONNECT_SETTLE_MS));
      }
      try {

        if (!writerRef.current) {
          throw new Error(formatMessage('led_serial_not_writable'));
        }

        const data = new TextEncoder().encode(message);
        await writerRef.current.write(data);

        console.log('LED command sent successfully:', message);
        // Remember what was written, even if the ACK later fails or times out.
        lastLedMessage = message;

        const response = await readAckLine();
        console.log('LED device response:', response);

        // // Close the port as soon as the response arrives, before handling it.
        // await disconnectSerial();

        if (response.startsWith('OK')) {
          return response;
        }

        if (response.startsWith('ERROR:')) {
          const code = response.slice('ERROR:'.length).trim();
          throw new Error(getLedErrorMessage(code));
        }

        throw new Error(
          formatMessage('led_error_unexpected_response')
        );
      } catch (error: any) {
        console.error('LED command failed:', JSON.stringify(message), error?.message);
        throw error;
      } finally {
        // Keep the port open between commands; close it only on a final command.
        if (final) {
          await disconnectSerial();
        }
      }
    };

    // Serialize commands so a second call cannot grab the reader
    // while an earlier command is still awaiting its ACK.
    const result = queueRef.current.then(run, run);

    // Keep the queue alive even when a command fails.
    queueRef.current = result.catch(() => undefined);

    // The original caller still receives the actual rejection.
    return result;
  },
  [connectSerial, disconnectSerial, readAckLine]
);

  useEffect(() => {
    return () => {
      disconnectSerial();
    };
  }, [disconnectSerial]);

  const isConnected = useCallback(() => !!writerRef.current, []);

  return { sendCommand, disconnectSerial, isConnected };
};
